import React, { ReactNode } from 'react';

/**
 * Icons of the MZČR host dialogs and states. `@meddihub/ui` has no icon set (its `Icon` is only a
 * chip around an icon), so the few glyphs live here - drawn once, sized by `size`.
 */
interface IconProps {
	size?: number;
	strokeWidth?: number;
}

const Svg = ({
	size,
	strokeWidth = 2,
	children,
}: IconProps & { size: number; children: ReactNode }) => (
	<svg
		viewBox='0 0 24 24'
		width={size}
		height={size}
		fill='none'
		stroke='currentColor'
		strokeWidth={strokeWidth}
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'>
		{children}
	</svg>
);

export const FolderIcon = ({
	open,
	size = 16,
}: IconProps & { open?: boolean }) => (
	<Svg size={size}>
		{open ? (
			<path d='m6 14 1.5-2.9A2 2 0 0 1 9.2 10H20a2 2 0 0 1 1.9 2.5l-1.5 6A2 2 0 0 1 18.5 20H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.7.9l.8 1.2a2 2 0 0 0 1.7.9H18a2 2 0 0 1 2 2v2' />
		) : (
			<path d='M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.7-.9l-.8-1.2A2 2 0 0 0 7.9 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2z' />
		)}
	</Svg>
);

export const FolderInputIcon = ({ size = 16 }: IconProps) => (
	<Svg size={size}>
		<path d='M2 9V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.7.9l.8 1.2a2 2 0 0 0 1.7.9H20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-1M2 13h10M9 16l3-3-3-3' />
	</Svg>
);

export const CheckIcon = ({ size = 12 }: IconProps) => (
	<Svg size={size} strokeWidth={3}>
		<path d='M20 6 9 17l-5-5' />
	</Svg>
);

export const InfoIcon = ({ size = 14 }: IconProps) => (
	<Svg size={size}>
		<circle cx='12' cy='12' r='10' />
		<path d='M12 16v-4M12 8h.01' />
	</Svg>
);

export const PlusIcon = ({ size = 16 }: IconProps) => (
	<Svg size={size}>
		<path d='M12 5v14M5 12h14' />
	</Svg>
);

export const SearchIcon = ({ size = 14 }: IconProps) => (
	<Svg size={size}>
		<circle cx='11' cy='11' r='7' />
		<path d='m20 20-3.5-3.5' />
	</Svg>
);

export const LinkIcon = ({ size = 13 }: IconProps) => (
	<Svg size={size}>
		<path d='M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7' />
		<path d='M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7' />
	</Svg>
);

// Glyphs of the empty states (bigger, thinner line)
export const IdCardIcon = ({ size = 28 }: IconProps) => (
	<Svg size={size} strokeWidth={1.8}>
		<rect x='3' y='5' width='18' height='14' rx='2' />
		<circle cx='9' cy='11' r='2' />
		<path d='M6 16c.6-1.2 1.7-2 3-2s2.4.8 3 2M14 10h4M14 13h3' />
	</Svg>
);

export const UserSearchIcon = ({ size = 28 }: IconProps) => (
	<Svg size={size} strokeWidth={1.8}>
		<circle cx='10' cy='7' r='4' />
		<path d='M2 21v-2a4 4 0 0 1 4-4h4' />
		<circle cx='17' cy='17' r='3' />
		<path d='m21 21-1.9-1.9' />
	</Svg>
);

export const CloudOffIcon = ({ size = 28 }: IconProps) => (
	<Svg size={size} strokeWidth={1.8}>
		<path d='m2 2 20 20' />
		<path d='M5.8 5.8A7 7 0 0 0 7.5 19H17a5 5 0 0 0 2.6-.7M22 15a5 5 0 0 0-4.6-5A7 7 0 0 0 9 5.2' />
	</Svg>
);

export const KeyIcon = ({ size = 28 }: IconProps) => (
	<Svg size={size} strokeWidth={1.8}>
		<circle cx='7.5' cy='15.5' r='4.5' />
		<path d='m10.7 12.3 9.8-9.8M16 7l3 3M14 9l2 2' />
	</Svg>
);
